import { useState } from 'react';
import { useCart, useUpdateCartItem, useRemoveFromCart, useClearCart } from '../../../hooks/useCart';
import { formatPriceFromGrosz } from '../../../utils/price';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onCheckoutClick: () => void;
    onToast: (message: string) => void;
}

// ============================================================
// FUNKCJE POMOCNICZE — 1:1 z działającego kodu referencyjnego
// (komponent CartDrawer z dostarczonego projektu)
// ============================================================

const getQuantityValue = (quantity: any): number => {
    if (typeof quantity === 'number') return quantity;
    if (typeof quantity === 'object' && quantity !== null) {
        return Number(quantity.value ?? quantity.quantity ?? 1);
    }
    return 1;
};

const getItemUnitPriceInGrosz = (item: any): number => {
    if (item.price) return parseFloat(item.price) || 0;
    if (item.totals?.line_subtotal) return parseFloat(item.totals.line_subtotal) || 0;
    return 0;
};

const getItemTotalPriceInGrosz = (item: any): number => {
    if (item.totals?.line_total) {
        return parseFloat(item.totals.line_total) || 0;
    }
    if (item.totals?.subtotal) {
        return parseFloat(item.totals.subtotal) || 0;
    }
    const unitPrice = getItemUnitPriceInGrosz(item);
    const qty = getQuantityValue(item.quantity);
    return unitPrice * qty;
};

const getItemName = (item: any): string => {
    return item.name || item.title || 'Produkt';
};

const getItemKey = (item: any): string => {
    return item.item_key || item.key || String(item.id || '');
};

const getItemImage = (item: any): string => {
    if (item.image?.src) return item.image.src;
    if (item.image?.url) return item.image.url;
    if (item.featured_image) return item.featured_image;
    return '';
};

const getVariations = (item: any): Record<string, string> => {
    const result: Record<string, string> = {};
    const variation = item.variation || item.meta?.variation || item.attributes || {};

    Object.entries(variation).forEach(([key, value]) => {
        if (value == null) return;

        const cleanKey = key.replace('attribute_', '').replace('pa_', '');

        let displayValue = '';
        if (typeof value === 'string') {
            displayValue = value;
        } else if (typeof value === 'object') {
            displayValue = (value as any).value ?? (value as any).option ?? String(value);
        } else {
            displayValue = String(value);
        }

        if (displayValue) {
            result[cleanKey] = displayValue;
        }
    });

    return result;
};

// ============================================================
// KOMPONENT
// ============================================================

export default function CartDrawer({ isOpen, onClose, onCheckoutClick, onToast }: Props) {
    const { data: cart, isLoading, refetch } = useCart();
    const updateCartItem = useUpdateCartItem();
    const removeFromCart = useRemoveFromCart();
    const clearCart = useClearCart();

    const [isUpdating, setIsUpdating] = useState(false);

    const handleUpdateQuantity = async (itemKey: string, newQuantity: number) => {
        if (newQuantity < 1) return;
        setIsUpdating(true);
        try {
            await updateCartItem.mutateAsync({ item_key: itemKey, quantity: newQuantity });
            await refetch();
            onToast('Zaktualizowano ilość produktu');
        } catch (error) {
            console.error('❌ Błąd aktualizacji ilości:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleRemoveItem = async (itemKey: string) => {
        setIsUpdating(true);
        try {
            await removeFromCart.mutateAsync(itemKey);
            await refetch();
            onToast('Usunięto produkt z koszyka');
        } catch (error) {
            console.error('❌ Błąd usuwania przedmiotu:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleClearCart = async () => {
        if (!window.confirm('Czy na pewno chcesz opróżnić koszyk?')) return;
        setIsUpdating(true);
        try {
            await clearCart.mutateAsync();
            await refetch();
            onToast('Koszyk został opróżniony');
        } catch (error) {
            console.error('❌ Błąd czyszczenia koszyka:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const itemCount = cart?.item_count || 0;
    const hasItems = !!cart?.items && cart.items.length > 0;
    const subtotalInGrosz = parseFloat(cart?.totals?.subtotal || '0');

    return (
        <div className={`store-drawer__overlay ${isOpen ? 'store-drawer__overlay--open' : ''}`} onClick={onClose}>
            <div
                className={`store-drawer ${isOpen ? 'store-drawer--open' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="store-drawer__header">
                    <h2 className="store-drawer__title">
                        Koszyk <span className="store-drawer__title-count">({itemCount})</span>
                    </h2>
                    <button className="store-drawer__close" onClick={onClose} aria-label="Zamknij">
                        ✕
                    </button>
                </div>

                <div className="store-drawer__body">
                    {isLoading ? (
                        <div className="store-modal__loading">
                            <div className="spinner">
                                <div className="cube1"></div>
                                <div className="cube2"></div>
                            </div>
                        </div>
                    ) : !hasItems ? (
                        <div className="store-drawer__empty">
                            <div className="store-drawer__empty-icon">🛒</div>
                            <p>Twój koszyk jest pusty</p>
                        </div>
                    ) : (
                        cart!.items.map((item: any) => {
                            const itemKey = getItemKey(item);
                            const itemName = getItemName(item);
                            const itemQty = getQuantityValue(item.quantity);
                            const imageSrc = getItemImage(item);
                            const variations = getVariations(item);
                            const variationEntries = Object.entries(variations);

                            const unitPriceInGrosz = getItemUnitPriceInGrosz(item);
                            const totalPriceInGrosz = getItemTotalPriceInGrosz(item);

                            return (
                                <div key={itemKey} className="store-drawer__item">
                                    <div className="store-drawer__item-image">
                                        {imageSrc ? <img src={imageSrc} alt={itemName} /> : <span>📦</span>}
                                    </div>

                                    <div className="store-drawer__item-info">
                                        <p className="store-drawer__item-name">{itemName}</p>

                                        {variationEntries.length > 0 && (
                                            <div className="store-drawer__item-variations">
                                                {variationEntries.map(([key, value]) => (
                                                    <span key={key}>
                                                        {key}: {value}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="store-drawer__item-price">
                                            <span>{formatPriceFromGrosz(unitPriceInGrosz)}</span>
                                            <span className="store-drawer__item-qty-label">× {itemQty}</span>
                                            <span>= {formatPriceFromGrosz(totalPriceInGrosz)}</span>
                                        </div>
                                    </div>

                                    <div className="store-drawer__item-controls">
                                        <div className="store-drawer__item-quantity">
                                            <button
                                                onClick={() => handleUpdateQuantity(itemKey, itemQty - 1)}
                                                disabled={isUpdating}
                                                aria-label="Zmniejsz ilość"
                                            >
                                                −
                                            </button>
                                            <span>{itemQty}</span>
                                            <button
                                                onClick={() => handleUpdateQuantity(itemKey, itemQty + 1)}
                                                disabled={isUpdating}
                                                aria-label="Zwiększ ilość"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <button
                                            className="store-drawer__item-remove"
                                            onClick={() => handleRemoveItem(itemKey)}
                                            disabled={isUpdating}
                                            aria-label="Usuń produkt"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {hasItems && (
                    <div className="store-drawer__footer">
                        <div className="store-drawer__total">
                            <span>Razem:</span>
                            <span>{formatPriceFromGrosz(subtotalInGrosz)}</span>
                        </div>

                        <div className="store-drawer__actions">
                            <button className="store-drawer__clear-btn" onClick={handleClearCart} disabled={isUpdating}>
                                Opróżnij
                            </button>
                            <button
                                className="store-drawer__checkout-btn"
                                onClick={onCheckoutClick}
                                disabled={!hasItems}
                            >
                                Przejdź do kasy
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
